import { useRef, useState } from "react";
import { Play, Pause, Sparkles, Bluetooth, Sun, Lightbulb, MapPin } from "lucide-react";

const IMG = "/media/eclipse.jpeg";
const VIDEO = "/media/eclipse-timelapse.mp4";
const POSTER = "/media/eclipse-poster.jpg";

const FEATURES = [
  { icon: Sun, label: "Red light therapy panels" },
  { icon: Bluetooth, label: "Bluetooth audio" },
  { icon: Lightbulb, label: "LED interior & exterior lighting" },
];

export default function FeaturedInstall() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      id="featured"
      data-testid="featured-section"
      className="relative overflow-hidden bg-[#0E0C0C] text-[#F3EFEA]"
    >
      {/* ambient red glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#C1121F]/20 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#7A0C15]/20 blur-[160px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="max-w-3xl">
          <p
            className="font-accent text-xs uppercase tracking-[0.22em] font-semibold mb-6"
            style={{ color: "#E23B4A" }}
          >
            <Sparkles size={13} strokeWidth={1.5} className="inline mr-2 -mt-1" />
            Featured Installation
          </p>
          <h2 className="font-serif-display font-light text-4xl md:text-6xl leading-[1.03] tracking-tight text-[#F7F4EF]">
            The Sun Home Eclipse
            <br />
            <span className="italic" style={{ color: "#E7663B" }}>
              a study in luxury.
            </span>
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-[#C9C1B8] max-w-2xl">
            One of the most refined models we&apos;ve assembled to date — installed for Steven in
            New Albany. Full-spectrum red light panels, immersive Bluetooth audio, and layered LED
            lighting inside and out. Watch the entire build come together, start to finish.
          </p>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                data-testid={`featured-feature-${f.label.split(" ")[0].toLowerCase()}`}
                className="flex items-center gap-3"
              >
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full border"
                  style={{ borderColor: "rgba(226,59,74,0.4)", color: "#E7663B" }}
                >
                  <f.icon size={16} strokeWidth={1.5} />
                </span>
                <span className="font-accent text-sm text-[#E4DDD4]">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          {/* Still render image */}
          <figure className="lg:col-span-7 relative overflow-hidden rounded-2xl border border-white/10 group">
            <img
              src={IMG}
              alt="Sun Home Eclipse infrared sauna with red light panels installed in a luxury home"
              className="w-full h-[360px] md:h-full min-h-[420px] object-cover img-zoom"
              data-testid="featured-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <figcaption className="absolute bottom-6 left-6 right-6 flex items-center gap-2 text-white/90">
              <MapPin size={14} strokeWidth={1.5} style={{ color: "#E7663B" }} />
              <span className="font-accent text-xs tracking-wider uppercase">
                Sun Home Eclipse · New Albany, OH
              </span>
            </figcaption>
          </figure>

          {/* Time-lapse video */}
          <div className="lg:col-span-5 relative overflow-hidden rounded-2xl border border-white/10 bg-black">
            <video
              ref={videoRef}
              data-testid="featured-video"
              className="w-full h-[420px] md:h-full min-h-[420px] object-cover"
              src={VIDEO}
              poster={POSTER}
              autoPlay
              muted
              loop
              playsInline
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />

            <div className="absolute top-5 left-5">
              <span
                className="font-accent text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full backdrop-blur-md"
                style={{ background: "rgba(193,18,31,0.25)", color: "#FBE7E4", border: "1px solid rgba(226,59,74,0.4)" }}
              >
                Time-lapse · Full Build
              </span>
            </div>

            <button
              data-testid="featured-video-toggle"
              onClick={toggle}
              aria-label={playing ? "Pause video" : "Play video"}
              className="absolute bottom-5 right-5 flex h-12 w-12 items-center justify-center rounded-full backdrop-blur-md transition-transform hover:scale-105"
              style={{ background: "rgba(226,59,74,0.9)", color: "#fff" }}
            >
              {playing ? <Pause size={18} strokeWidth={1.5} /> : <Play size={18} strokeWidth={1.5} className="ml-0.5" />}
            </button>
          </div>
        </div>

        <div className="mt-10">
          <a
            href="#book"
            data-testid="featured-cta"
            className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-accent text-sm tracking-wide transition-colors"
            style={{ background: "#E23B4A", color: "#fff" }}
          >
            Plan a luxury install like this
          </a>
        </div>
      </div>
    </section>
  );
}
